import { createContext, useContext, useState } from "react";
import Modal from "react-bootstrap/Modal";
import "./ModalProvider.scss";

const ModalContext = createContext();

// Default Bootstrap modal props that every modal will inherit
const defaultModalProps = {
  centered: true,
  className: "",
  animation: false,
  fullscreen: "md-down",
  backdrop: "static",
  contentClassName: "",
  dialogClassName: "",
  scrollable: false,
};

export const ModalProvider = ({ children }) => {
  // Array of modals, supports stacked or single depending on how openModal is called
  const [modals, setModals] = useState([]);

  /**
   * Opens a new modal.
   * - Accepts a component (`ContentComponent`),
   *   optional props for that component (`contentProps`),
   *   optional Bootstrap modal props (`modalProps`),
   *   and an options object { stack: true|false }.
   * - If `replace: true`, replaces all existing modals (single modal mode).
   * - If `replace: false`, adds modal to stack (multiple modals).
   */
  const openModal = (
    ContentComponent,
    contentProps = {},
    modalProps = {},
    { replace = true, id = null } = {},
  ) => {
    setModals((prev) => {
      const newModal = {
        id,
        ContentComponent,
        contentProps,
        modalProps: { ...defaultModalProps, ...modalProps },
      };

      return replace ? [newModal] : [...prev, newModal]; // either replace existing modal or push new modal
    });
  };

  /**
   * Closes the topmost modal.
   * - Removes the last modal in the stack.
   * - If only one modal exists, clears everything.
   */
  const closeModal = () => {
    setModals((prev) => prev.slice(0, -1)); // pop last modal
  };

  /**
   * Closes all modals at once.
   */
  const closeAllModals = () => {
    setModals([]);
  };

  return (
    <ModalContext.Provider
      value={{ modals, openModal, closeModal, closeAllModals }}
    >
      {children}
      <BootstrapModal />
    </ModalContext.Provider>
  );
};

const BootstrapModal = () => {
  const { modals, closeModal } = useModal();

  // Nothing to render if no modals are open
  if (modals.length === 0) return null;

  return (
    <>
      {modals.map((modal, i) => (
        <Modal
          key={i}
          id={modal.id}
          show={true}
          onHide={closeModal}
          {...modal.modalProps}
        >
          <modal.ContentComponent
            {...modal.contentProps}
            closeModal={closeModal}
          />
        </Modal>
      ))}
    </>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};
